import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  Phone, 
  Mail, 
  MessageCircle, 
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  Calendar,
  Warehouse,
  Users,
  Store,
  FolderArchive,
  Heart,
  Trophy
} from "lucide-react";

const solutionItems = [
  { label: "Exhibitions & Trade Shows", path: "/exhibition", icon: Calendar },
  { label: "Events & Wedding", path: "/events", icon: Heart },
  { label: "Warehousing", path: "/warehousing", icon: Warehouse },
  { label: "Labour Rest Area", path: "/labour", icon: Users },
  { label: "Sales Centre", path: "/sales", icon: Store },
  { label: "Sports Pavillions", path: "/sports", icon: Trophy },
];

const navLinks = [
  { label: "Videos", path: "/videos" },
  { label: "Gallery", path: "/gallery" },
  { label: "Contact", path: "/contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleMouseEnter = () => {
    if (window.innerWidth > 768) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsSolutionsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      timeoutRef.current = setTimeout(() => setIsSolutionsOpen(false), 200);
    }
  };

  const linkClass = (path) =>
    `text-[10px] lg:text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 flex items-center ${
      location.pathname === path ? "text-white" : "text-gray-400 hover:text-white"
    }`;

  const actionBtnClass = "flex items-center gap-1.5 px-2.5 py-2 rounded-full border border-white/10 bg-white/5 text-[8px] font-bold uppercase tracking-widest transition-all hover:bg-white/10 hover:text-white text-gray-400";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 z-[120]">
            <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_300/v1741002198/Logo_llsnwq.png"
                  alt="BA Tents Logo"
                  width={300}
                  height={84}
                  className="w-full h-full object-contain"
                  loading="eager"
                  decoding="async"
                />
            </div>
            <span className="text-white font-black tracking-[0.3em] text-xs md:text-sm shrink-0">BA TENTS</span>
          </Link>

          {/* --- DESKTOP NAVIGATION --- */}
          <nav className="hidden lg:flex items-center gap-6">
            <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <button 
                onClick={() => navigate("/solutions")} 
                className={`${linkClass("/solutions")} gap-1`}
              >
                Solutions <ChevronDown className={`w-3 h-3 transition-transform ${isSolutionsOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {isSolutionsOpen && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: 10 }} 
                    className="absolute top-full left-0 mt-4 w-64 bg-[#0D0D0D] border border-white/10 rounded-2xl p-2 shadow-2xl"
                  >
                    {solutionItems.map((item) => (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 text-gray-300 hover:text-white transition-all uppercase text-[10px] font-bold tracking-widest"
                      >
                        <item.icon className="w-4 h-4 text-[#bd6aff]" /> {item.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} className={linkClass(link.path)}>{link.label}</Link>
            ))}
          </nav>

          {/* --- DESKTOP ACTIONS --- */}
          <div className="hidden lg:flex items-center gap-1.5">
            <a href="tel:+971588243660" className={actionBtnClass}>
                <Phone className="w-2.5 h-2.5" /> <span className="hidden xl:inline">Call</span>
            </a>
            <a href="mailto:info@batents.ae" className={actionBtnClass}>
                <Mail className="w-2.5 h-2.5" /> <span className="hidden xl:inline">Email</span>
            </a>
            <a 
              href="https://wa.me/971588243660" 
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-full border border-green-500/20 bg-green-500/5 text-[8px] font-bold uppercase tracking-widest text-green-400 hover:bg-green-500/10 transition-all"
            >
                <MessageCircle className="w-2.5 h-2.5" /> <span className="hidden xl:inline">WhatsApp</span>
            </a>
          </div>

          <button 
            className="lg:hidden text-white p-2" 
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* --- MOBILE SIDEBAR --- */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div 
            initial={{ x: "100%" }} 
            animate={{ x: 0 }} 
            exit={{ x: "100%" }} 
            transition={{ type: "spring", damping: 25, stiffness: 200 }} 
            className="fixed inset-0 bg-[#080808] z-[150] lg:hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                    <img src="https://res.cloudinary.com/dizdlq9fo/image/upload/v1741002198/Logo_llsnwq.png" className="w-8 h-8" alt="Logo" />
                    <span className="text-white font-black tracking-widest text-xs">BA TENTS</span>
                </div>
                <button onClick={() => setMobileOpen(false)} className="text-white p-2 bg-white/5 rounded-full">
                    <X className="w-6 h-6" />
                </button>
            </div>
            
            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-8">
              
              {/* Quick Contact Grid */}
              <div className="flex w-full overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                <a href="https://wa.me/971588243660" className="flex flex-1 flex-col items-center justify-center gap-1.5 py-4 text-green-400 border-r border-white/10 transition-colors hover:bg-green-500/10" aria-label="Chat on WhatsApp">
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-[8px] font-black uppercase tracking-widest">Chat</span>
                </a>
                <a href="tel:+971588243660" className="flex flex-1 flex-col items-center justify-center gap-1.5 py-4 text-white border-r border-white/10 transition-colors hover:bg-white/10" aria-label="Call BA Tents">
                  <Phone className="w-5 h-5 text-[#bd6aff]" />
                  <span className="text-[8px] font-black uppercase tracking-widest">Call</span>
                </a>
                <a href="mailto:info@batents.ae" className="flex flex-1 flex-col items-center justify-center gap-1.5 py-4 text-white transition-colors hover:bg-white/10" aria-label="Send an Email">
                  <Mail className="w-5 h-5 text-[#bd6aff]" />
                  <span className="text-[8px] font-black uppercase tracking-widest">Email</span>
                </a>
              </div>

              {/* Navigation List */}
              <div className="space-y-10">
                <div>
                    <p className="text-gray-500 text-[10px] font-black tracking-[0.3em] uppercase mb-6">EXPLORE</p>
                    <div className="flex flex-col gap-6">
                        <Link to="/" className="text-3xl font-light text-white uppercase tracking-tighter" onClick={() => setMobileOpen(false)}>Home</Link>
                        
                        <div className="flex flex-col gap-4">
                            <Link to="/solutions" className="text-3xl font-light text-white uppercase tracking-tighter flex items-center justify-between group" onClick={() => setMobileOpen(false)}>
                                Solutions
                                <ArrowUpRight className="w-6 h-6 text-white/30 group-hover:text-white transition-colors" />
                            </Link>
                            <div className="grid grid-cols-1 gap-1">
                                {solutionItems.map((item) => (
                                    <Link 
                                      key={item.path} 
                                      to={item.path} 
                                      className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.05] text-gray-400 text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-all" 
                                      onClick={() => setMobileOpen(false)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <item.icon className="w-4 h-4 text-[#bd6aff]" />
                                            {item.label}
                                        </div>
                                        <ChevronRight className="w-3 h-3 text-white/10" />
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {navLinks.map((link) => (
                           <Link 
                            key={link.path} 
                            to={link.path} 
                            className="text-3xl font-light text-white uppercase tracking-tighter flex items-center justify-between" 
                            onClick={() => setMobileOpen(false)}
                           >
                            {link.label}
                            {link.icon && <link.icon className="w-5 h-5 text-white/20" />}
                           </Link>
                        ))}
                    </div>
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;